'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

/**
 * Provides React Query context for client components.
 * Creates a new QueryClient per component instance to avoid sharing state across requests.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            // Live data arrives over SSE (see useEventSource), which invalidates
            // the affected keys precisely. Focus/reconnect refetches would just
            // re-pull everything the socket already keeps fresh — a redundant
            // refetch storm on the story hub during generation. Trust the socket.
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
